import dataLayerPush from 'tridion.web.ui/src/app/analytics/dataLayerPush';
import { Video } from './Video';
import config from './../../config';
import { VideoUi } from './VideoUi';
import { VideoUrls } from './VideoUrls';
import { Ga4Data } from './Ga4Data';
import { forEach, split } from 'lodash';


/**
 * Essity Video Player
 */
export default class VideoPlayer {
 
    private static readonly AzureSmoothUrl = /.*\.ism\/manifest/i;
    private static readonly DashUrl = /format=mpd-|mpd-time-csf/i;
    private static readonly HlsUrl = /format=m3u8-|\.m3u8/i;
    private static readonly TrackPlayPercentage = 25;
    private static UniqueId = 0;
    private static licenseKey = config.licenseKey;
    
    ga4Interactions = {
        play: 'ga4_video_start',
        //pause: 'ga4_video_pause',
        progress: 'ga4_video_progress',
        complete: 'ga4_video_complete',
    };

    /**
     * `<div>` containing the {@link VideoPlayer}.
     */
    protected element: HTMLDivElement;
    
    /**
     * Underlying JWPlayer.
     *
     * @see https://developer.jwplayer.com/jw-player/docs/javascript-api-reference/
     */
    protected jwplayer: jwplayer.JWPlayer;
    //protected sharingPlugin: any;

    

    // tslint:disable:cyclomatic-complexity max-func-body-length

    /**
     * If you supply a Streaming URL for Azure Media Services, this will automatically:
     * - Set {@link VideoUrls.dash}, if not already set.
     * - Set {@link VideoUrls.hls}, if not already set.
     * - Add the appropriate suffix (`(format=mpd-time-csf)`)
     *   to {@link VideoUrls.dash}, if not already added.
     * - Add the appropriate suffix (`(format=m3u8-aapl-v3)` or `(format=m3u8-aapl)`)
     *   to {@link VideoUrls.hls}, if not aleady added.
     *
     * @param url
     * @param upgradeToHttps `true` to upgrade `http://` URLs to `https://`.
     *                       Defaults to `true` when the current page is using `https://`.
     */
    
    static calculateVideoUrls(url: string | VideoUrls, upgradeToHttps?: boolean) {
        var dash: string, hls: string, progressive: string;

        if (url) {
            if (typeof url === 'string') {
                if (url.match(VideoPlayer.HlsUrl)) {
                    hls = url;
                } else if (url.match(VideoPlayer.DashUrl)) {
                    dash = url;
                } else {
                    progressive = url;
                }
            } else {
                dash = url.dash;
                hls = url.hls;
                progressive = url.progressive;
            }

            let azureSmooth: string, match: RegExpMatchArray;

            // tslint:disable:no-conditional-assignment

            if (progressive && (match = progressive.match(VideoPlayer.AzureSmoothUrl))) {
                azureSmooth = match[0];
                progressive = undefined;
            } else if (hls && (match = hls.match(VideoPlayer.AzureSmoothUrl))) {
                azureSmooth = match[0];
            } else if (dash && (match = dash.match(VideoPlayer.AzureSmoothUrl))) {
                azureSmooth = match[0];
            }

            // tslint:enable:no-conditional-assignment

            if (azureSmooth) {
                if (!dash || !dash.match(VideoPlayer.DashUrl)) {
                    dash = azureSmooth + '(format=mpd-time-csf)';
                }
                if (!hls || !hls.match(VideoPlayer.HlsUrl)) {
                    hls = azureSmooth + '(format=m3u8-aapl)';
                }
            }

            if (
                upgradeToHttps ||
                (upgradeToHttps !== false && location.protocol === 'https:')
            ) {
                if (dash) {
                    dash = dash.replace(/^http:/, 'https:');
                }
                if (hls) {
                    hls = hls.replace(/^http:/, 'https:');
                }
                if (progressive) {
                    progressive = progressive.replace(/^http:/, 'https:');
                }
            }
        }

        return {
            dash: dash || undefined,
            hls: hls || undefined,
            progressive: progressive || undefined
        } as VideoUrls;
    }

    // tslint:enable

    /**
     * Get the `id` of an element, and set an `id` on the element if one doesn't exist.
     */
    static getOrCreateId(element: HTMLElement): string {
        var id = element.id;

        if (!id) {
            id = 'VideoPlayer' + ++VideoPlayer.UniqueId;
            element.id = id;
        }

        return id;
    }

    

    /**
     * Get {@link Video} data from HTML attributes:
     * - data-video-aspect-ratio="{@link Video.aspectRatio}"
     * - data-video-image="{@link Video.image}"
     * - data-video-name="{@link Video.name}"
     * - data-video-url="{@link Video.url}"
     * - data-video-url-dash="{@link VideoUrls.dash}"
     * - data-video-url-hls="{@link VideoUrls.hls}"
     * - data-video-url-progressive="{@link VideoUrls.progressive}"
     * - data-video-loop="{@link Video.loop}"
     * - data-video-loop-max-repeats="{@link Video.maxRepeats}"
     * - data-video-mute="{@link Video.mute}"
     * - data-video-controls="{@link Video.controls}"
     * - data-video-autoplay="{@link Video.autoplay}"
     * - data-video-analytics-disable="{@link Video.analytics.disable}"
     * - data-video-sharing-enable="{@link Video.sharing}"
     * - data-video-sharing-sites="{@link Video.sites}"
     * - data-video-sharing-heading="{@link Video.heading}"
     */
    static getVideoData(element: HTMLElement): Video {
        
        var url = VideoPlayer.calculateVideoUrls(element.getAttribute('data-video-url')),
            urls = VideoPlayer.calculateVideoUrls({
                dash: element.getAttribute('data-video-url-dash'),
                hls: element.getAttribute('data-video-url-hls'),
                progressive: element.getAttribute('data-video-url-progressive')
            });
            var sitesArray: string[];
            var checkMaxRepeats = Number(element.getAttribute('data-video-loop-max-repeats')) || 5;
          
            if(checkMaxRepeats > 10) {
                checkMaxRepeats = 10;
            }  

            if(checkMaxRepeats <= 0) {
                checkMaxRepeats = 5;
            } 
        
            var tempSites = element.getAttribute('data-video-sharing-sites');          
           
                if (tempSites) {                  
                    sitesArray = tempSites.toLowerCase().split(" ");
                }
                else {
                    sitesArray = ['facebook', 'twitter', 'linkedin', 'pinterest', 'tumblr', 'reddit', 'email'];
                }
       
        return {
            
            url: {
                dash: urls.dash || url.dash || undefined,
                hls: urls.hls || url.hls || undefined,
                progressive: urls.progressive || url.progressive || undefined
            },
            aspectRatio: element.getAttribute('data-video-aspect-ratio') || undefined,
            image: element.getAttribute('data-video-image') || undefined,
            analytics: {
                name: element.getAttribute('data-video-name') || undefined,
                category: element.getAttribute('data-video-category') || undefined,
                disable: element.getAttribute('data-video-analytics-disable') || undefined
            },
            loop: element.getAttribute('data-video-loop') || undefined,
            maxRepeats: checkMaxRepeats,
            currentPlay: 0,
            autoplay: element.getAttribute('data-video-autoplay') || undefined,
            controls: element.getAttribute('data-video-controls') || undefined,
            mute: element.getAttribute('data-video-mute') || undefined,
            sharing: { 
                
                sites: sitesArray || undefined,
                heading: element.getAttribute('data-video-sharing-heading') || "Share this video",
                enabled: element.getAttribute('data-video-sharing-enable') || undefined,

            }
            

        };
    
    }

    /**
     * Get the name of the {@link Video},
     * or the {@link Video}’s URL when no name is provided.
     */
    protected static getNameOrUrl(video: Video) {
        
        var analytics = video.analytics,
            nameOrUrl = analytics && analytics.name;

        if (!nameOrUrl) {
            let url = video.url;
            if (url) {
                if (typeof url === 'string') {
                    nameOrUrl = url;
                } else {
                    nameOrUrl = url.progressive || url.hls || url.dash;
                }
            }
        }
    
        
        return nameOrUrl;
    }

    protected static getName(video: Video) {
        
        let getVideoTitle = '';
      
        var analytics = video.analytics,
            videoName = analytics.name;
        
            if(videoName) {
                getVideoTitle = videoName;
            }
            else {
                getVideoTitle = '';
            }
        
        return getVideoTitle;
    }

    protected static getUrl(video: Video) {

        let videoUrl = '';
       
            let url = video.url;
            if (url) {
                if (typeof url === 'string') {
                    videoUrl = url;
                } else {
                    videoUrl = url.progressive || url.hls || url.dash;
                }
            }
        

        return videoUrl;
    }


    /**
     * Turn a `<div>` into a {@link VideoPlayer}.
     *
     * @param div
     * @param video If not specified, uses the `data-video-` attributes on `div`.
     *              See {@link VideoPlayer.getVideoData}.
     * @param ui    User Interface options.
     */
    constructor(div: HTMLDivElement, video?: Video, ui?: VideoUi) {
        this.element = div;
   
        
                if (video) {
                video = {
                    url: VideoPlayer.calculateVideoUrls(video.url),
                    autoplay: video.autoplay || undefined,
                    loop: video.loop || undefined,
                    maxRepeats: video.maxRepeats || 5,
                    currentPlay: video.currentPlay || 0,
                    aspectRatio: video.aspectRatio || undefined,
                    controls: video.controls || undefined,
                    mute: video.mute || undefined,
                    image: video.image || undefined,
                    analytics: {
                        name: (video.analytics && video.analytics.name) || undefined,
                        category: (video.analytics && video.analytics.category) || undefined,
                        disable: (video.analytics && video.analytics.disable) || undefined
                    },
                    sharing: {
                        enabled: video.sharing.enabled || undefined,
                        sites: video.sharing.sites || undefined,
                        heading: video.sharing.heading || undefined,
                    },
                   
                };
            } else {
                video = VideoPlayer.getVideoData(div);
            }
            
        
       


       
        this.setupJwplayer(video, ui);
        this.setupRepeatCounter(video);
             
       
        if (video.analytics.disable !== 'true') {
            
            if(((video.autoplay == 'false' ) && (video.loop == 'false')) || ((video.autoplay == 'false' ) && (video.loop == 'true'))) {
               
                this.setupAnalytics(video);
            }
        }
    }
    
    /**
     * Gets the state.
     */
    getVideoState() {
        return this.jwplayer.getState();
    }
    /**
     * Pause the video.
     */
    pause() {
        this.jwplayer.pause(true);
    }
    

    /**
     * Play the video.
     */
    play() {
        this.jwplayer.play(true);
   
    }
    /**
     * Play video on ready.
     */
    playOnReady(){
        this.jwplayer.on('ready', () => {
        
           // this.sharingPlugin = this.jwplayer.getPlugin('sharing');
            this.jwplayer.play(true);
        });
    }

    
    /**
     * Stop the video.
     */
    stop() {
        this.jwplayer.stop();
    }

    

    /**
     * Remove the video player from the page.
     *
     * In addition to {@link stop}, this stops buffering and removes event listeners.
     */
    destroy() {
        this.jwplayer.remove();
    }

    // tslint:disable:max-func-body-length

    /**
     * Create the options which will be passed to JWPlayer.
     *
     * @see https://developer.jwplayer.com/jw-player/docs/developer-guide/
     */
    protected createJwplayerOptions(video: Video, ui?: VideoUi): any {
       
        var versionMatch = jwplayer.version && jwplayer.version.match(/^\d+\.\d+\.\d+/),
            version = (versionMatch && versionMatch[0]) || '8.34.5';
            var options: any;
            //*** To replace twitter with X we need to create a custom array and replace twitter with the new X array in the sites array */
           // const media = require('./media/twitter.svg');

              
            if(video.sharing.sites.includes("twitter")) {
                /*let renameToX = {} as any; 
                renameToX = {
                    icon: media,
                    src: "https://x.com/intent/post?url=",
                    label: "X",
                };*/
                
                var indexOfTwitter = video.sharing.sites.indexOf("twitter");
                
                //remove twitter
                video.sharing.sites.splice(indexOfTwitter, 1)
                
                //add X at the same index as twitter had
                //video.sharing.sites.splice(indexOfTwitter, 0, renameToX);
                
            }
            
            
            //***   */

            //To be able to disable share feature I had to create the switch function. 
            switch(video.sharing.enabled){
                case "true":
                    options = {
                        // License Key
                        key: VideoPlayer.licenseKey,
            
                        // User Interface
                        width: '100%',
                        aspectratio: video.aspectRatio || '16:9',
                        autostart: video.autoplay || false,
                        loop: video.autoplay || false,
                        repeat: video.loop || false,
                        controls: video.controls !== 'false',
                        mute: video.mute === 'true',

                        // Stop playback when the player is not inside the viewable part of the screen.
                        autoPause: {
                            viewable: true,
                            pauseAd: true,
                        },
            
                        // JWPlayer Features
                        androidhls: true,
                        hlshtml: true,
            
                        // Handle CORS with Flash, since we can't add crossdomain.xml in Static CDN.
                        flashplayer:
                            'https://ssl.p.jwpcdn.com/player/v/' + version + '/jwplayer.flash.swf',
            
                        playlist: [
                            {
                                // Image
                                image: video.image || undefined,
            
                                // Video URLs
                                sources: this.getSources(video.url as VideoUrls)
                            }
                        ],

                        // Enables the share button in the player control area
                        sharing: {
                            enabled: video.sharing.enabled,
                            sites: video.sharing.sites,
                            heading: video.sharing.heading || "Share this video"
                        
                        },                                                      
                    }

                break;

               default:
                    options = {
                        // License Key
                        key: VideoPlayer.licenseKey,
            
                        // User Interface
                        width: '100%',
                        aspectratio: video.aspectRatio || '16:9',
                        autostart: video.autoplay || false,
                        loop: video.autoplay || false,
                        repeat: video.loop || false,
                        controls: video.controls !== 'false',
                        mute: video.mute === 'true',

                        // Stop playback when the player is not inside the viewable part of the screen.
                        autoPause: {
                            viewable: true,
                            pauseAd: true,
                        },
                                   
                        // JWPlayer Features
                        androidhls: true,
                        hlshtml: true,
            
                        // Handle CORS with Flash, since we can't add crossdomain.xml in Static CDN.
                        flashplayer:
                            'https://ssl.p.jwpcdn.com/player/v/' + version + '/jwplayer.flash.swf',
            
                        playlist: [
                            {
                                // Image
                                image: video.image || undefined,
            
                                // Video URLs
                                sources: this.getSources(video.url as VideoUrls)
                            }
                        ],
                        
                        
            
                     
                    }
               
            }

        
        if (ui) {
            let skin = ui.skin;
            if (skin) {
                options.skin = {
                    name: skin
                };
            }
        }

        return options;
       
    }
    // tslint:enable

    /**
     * Order {@link VideoUrls} from best to worst playback quality and performance.
     */
    protected getSources(url: VideoUrls) {
       
        var sources: { file: string; type?: string }[] = [];

        var hls = url.hls;
        if (hls) {
            sources.push({
                file: hls,
                type: 'application/vnd.apple.mpegurl'
            });
        }

        // FIXME: Consider placing DASH before HLS.
        var dash = url.dash;
        if (dash) {
            sources.push({
                file: dash,
                type: 'application/dash+xml'
            });
        }

        var progressive = url.progressive;
        if (progressive) {
            sources.push({
                file: progressive
            });
        }
        
        return sources;
        
    }
    
    protected setupJwplayer(video: Video, ui?: VideoUi) {
        var id = VideoPlayer.getOrCreateId(this.element),
            options = this.createJwplayerOptions(video, ui);

        this.jwplayer = jwplayer(id).setup(options);
    }

    // Limit the amount of repeats from autoplay
    protected setupRepeatCounter(video: Video) {
       
        this.jwplayer.on('complete', () => {
            
            if(video.currentPlay >= video.maxRepeats) {                   
                this.jwplayer.stop();           
            }
            video.currentPlay++;
        });

    }

    protected setupAnalytics(video: Video) {

        var firstPlay = false;
        var passed25 = false;
        var passed50 = false;
        var passed75 = false;
        var playCompleted = false;
        var ga4data = new Ga4Data();

        var jwplayer = this.jwplayer,
            lastInteraction: string;

        var pushInteraction = (interaction: string, ga4Data: any | null, isGa4: boolean ) => {
            if (lastInteraction !== interaction) {
                lastInteraction = interaction;
                this.dataLayerPush(video, interaction, ga4Data, isGa4);
            }
        }
        jwplayer.on('play', (event) => {
            
            if(!firstPlay) {
               
                ga4data.ga4DataEventPlay.video_current_time = 0;
                ga4data.ga4DataEventPlay.video_duration = Math.floor(jwplayer.getDuration());
                ga4data.ga4DataEventPlay.video_percent = 0;
                ga4data.ga4DataEventPlay.video_title = VideoPlayer.getName(video);
                ga4data.ga4DataEventPlay.video_url = VideoPlayer.getUrl(video);
                ga4data.ga4DataEventPlay.visible = event.viewable;
                
            
                pushInteraction(this.ga4Interactions.play, ga4data.ga4DataEventPlay, true);
                firstPlay = !firstPlay;
            }
            pushInteraction('Play', null, false);
        });

        jwplayer.on('pause', (event) => {

        /***** This can be use to fire pause events for GA4,
         * it will also provide information about if the pause event was fired by viewability or interaction
         * According to Onur, there are currently no deamnd for this, but may come in the future when autoplay 
         * has been available for some time.
         * To enable again, remember to uncomment ga4DataEventPause in Ga4Data.ts
         * Also uncomment pause: 'ga4_video_pause', under ga4interactions declaration further up in this file.
 

           var duration = jwplayer.getDuration();
           var position = jwplayer.getPosition();
           var percentage = position * 100 / duration;
            
            ga4data.ga4DataEventPause.video_current_time = Math.floor(position);
            ga4data.ga4DataEventPause.video_percent = Math.round(percentage);            
            ga4data.ga4DataEventPause.video_duration = Math.floor(duration);             
            ga4data.ga4DataEventPause.video_title = VideoPlayer.getName(video);
            ga4data.ga4DataEventPause.video_url = VideoPlayer.getUrl(video);
            ga4data.ga4DataEventPause.visible = event.viewable;
            ga4data.ga4DataEventPause.video_pause_reason = event.pauseReason;

            pushInteraction(this.ga4Interactions.pause, ga4data.ga4DataEventPause, true);
            */

            pushInteraction('Pause', null, false);


        });

        jwplayer.on('time', (event) => {
          
            var percentage = event.position * 100 / event.duration,
                track = VideoPlayer.TrackPlayPercentage,
                trackPercentage = track * Math.floor(Math.round(percentage) / track);

            // ** added modulo calculation to prevent progress events from happening if 
            // ** seeker is put past milestones 25/50/75 % without actually watching past the milestone (if seeker was set to 90% progess 75% was still triggered)  //SEHPAST
            if ((trackPercentage > 0 && trackPercentage < 100) && (Math.floor(percentage % track) === 0)) { 
                
                if(trackPercentage === 25) {
                    pushInteraction('Progress ' + trackPercentage + '%', null, false);
                    if(!passed25) {
                        this.setParameters(trackPercentage,ga4data,video,event,jwplayer)
                        
                        pushInteraction(this.ga4Interactions.progress, ga4data.ga4DataEventProgress25, true)
                        passed25 = !passed25;
                    }
                }

                if(trackPercentage === 50) {
                    pushInteraction('Progress ' + trackPercentage + '%', null, false);
                    if(!passed50) {
                        this.setParameters(trackPercentage,ga4data,video,event,jwplayer)
                        pushInteraction(this.ga4Interactions.progress, ga4data.ga4DataEventProgress50, true)
                        passed50 = !passed50;
                    }
                }

                if(trackPercentage === 75) {
                    pushInteraction('Progress ' + trackPercentage + '%', null, false);
                    if(!passed75) {
                        this.setParameters(trackPercentage,ga4data,video,event,jwplayer)
                        pushInteraction(this.ga4Interactions.progress, ga4data.ga4DataEventProgress75, true)
                        passed75 = !passed75;
                    }
                }
            }

            // ** event callback is not available "on complete". So this is a workaraound as we still need to populate reired values to push to datalayer.
            if(trackPercentage === 100) { 
                if(!playCompleted) {
                    this.setParameters(trackPercentage,ga4data,video,event,jwplayer)
                    pushInteraction(this.ga4Interactions.complete, ga4data.ga4DataEventComplete, true)
                    playCompleted = !playCompleted
                }
            }            
        });
        jwplayer.on('complete', () => {
            pushInteraction('Complete', null, false);           
        });
    
    }

    private setParameters(trackPercentage: number,ga4data: Ga4Data,video: Video,event: jwplayer.TimeParam,jwplayer: jwplayer.JWPlayer){
        
        
        if(trackPercentage === 25 ){
            ga4data.ga4DataEventProgress25.video_percent = trackPercentage;
            ga4data.ga4DataEventProgress25.video_duration = Math.floor(jwplayer.getDuration());
            ga4data.ga4DataEventProgress25.video_url = VideoPlayer.getUrl(video);
            ga4data.ga4DataEventProgress25.video_title = VideoPlayer.getName(video);
            ga4data.ga4DataEventProgress25.visible = event.viewable;
            ga4data.ga4DataEventProgress25.video_current_time = Math.floor(event.position);
        }

        if(trackPercentage === 50){
            ga4data.ga4DataEventProgress50.video_percent = trackPercentage;
            ga4data.ga4DataEventProgress50.video_duration = Math.floor(jwplayer.getDuration());
            ga4data.ga4DataEventProgress50.video_url = VideoPlayer.getUrl(video);
            ga4data.ga4DataEventProgress50.video_title = VideoPlayer.getName(video);
            ga4data.ga4DataEventProgress50.visible = event.viewable;
            ga4data.ga4DataEventProgress50.video_current_time = Math.floor(event.position);
        }
        if(trackPercentage === 75){
            ga4data.ga4DataEventProgress75.video_percent = trackPercentage;
            ga4data.ga4DataEventProgress75.video_duration = Math.floor(jwplayer.getDuration());
            ga4data.ga4DataEventProgress75.video_url = VideoPlayer.getUrl(video);
            ga4data.ga4DataEventProgress75.video_title = VideoPlayer.getName(video);
            ga4data.ga4DataEventProgress75.visible = event.viewable;
            ga4data.ga4DataEventProgress75.video_current_time = Math.floor(event.position);            
        }
        if(trackPercentage === 100){
            ga4data.ga4DataEventComplete.video_percent = 100;
            ga4data.ga4DataEventComplete.video_duration = Math.floor(jwplayer.getDuration());
            ga4data.ga4DataEventComplete.video_url = VideoPlayer.getUrl(video);
            ga4data.ga4DataEventComplete.video_title = VideoPlayer.getName(video);
            ga4data.ga4DataEventComplete.visible = event.viewable;
            ga4data.ga4DataEventComplete.video_current_time = Math.floor(jwplayer.getDuration());
        }
    
    }
  

    private dataLayerPush(video: Video, interaction: string, pushGa4Data: any | null, isGa4: boolean) {

        
       
        if(isGa4) {
            
            let dataLayerEventGa4: any;
            if(isGa4) {           
                dataLayerEventGa4 = {
                    event: interaction,
                    ga4_data: pushGa4Data,

            }};
            dataLayerPush(dataLayerEventGa4);
        }
    else {
        let analytics = video.analytics,
            category = analytics && analytics.category,
            nameOrUrl = VideoPlayer.getNameOrUrl(video),
            

            dataLayerEvent = {
                event: 'gaEvent',
                eventCategory:'video',
                eventAction: interaction,
                eventLabel: nameOrUrl
            };
           
            dataLayerPush(dataLayerEvent);
        }
       

        
    

    }

}
// tslint:disable:max-file-line-count